import { CompactNumber } from "@/shared/ui/CompactNumber";
import { Card, CardContent } from "@/components/ui/card";
import {
  BarChart3Icon,
  ClockIcon,
  InfoIcon,
  ShoppingCartIcon,
} from "lucide-react";

type OperationsReportsSummaryCardsProps = {
  avgPrepTime: number;
  totalOrders: number;
  peakHour: string | null;
  avgOrdersPerHour: number;
  isLoading?: boolean;
};

export function OperationsReportsSummaryCards({
  avgPrepTime,
  totalOrders,
  peakHour,
  avgOrdersPerHour,
  isLoading,
}: OperationsReportsSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="rounded-xl border bg-card">
            <CardContent className="">
              <div className="h-10 animate-pulse rounded bg-muted" />
              <div className="mt-2 h-6 w-16 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {/* AVG PREP TIME */}
      <Card className="rounded-xl border border-blue-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
            <InfoIcon className="size-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ເວລາເຕີມສະເລ່ຍ</p>
            <p className="font-semibold text-xl tabular-nums">
              {avgPrepTime.toFixed(1)} ນາທີ
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TOTAL ORDERS */}
      <Card className="rounded-xl border border-green-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-green-500/10">
            <ShoppingCartIcon className="size-5 text-green-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຈຳນວນອໍເດີລວມ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={totalOrders} />
            </p>
          </div>
        </CardContent>
      </Card>

      {/* PEAK HOUR */}
      <Card className="rounded-xl border border-orange-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10">
            <ClockIcon className="size-5 text-orange-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຊົ່ວໂມງສູງສຸດ</p>
            <p className="font-semibold text-xl tabular-nums">
              {peakHour ?? "–"}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* AVG ORDERS/HR */}
      <Card className="rounded-xl border border-purple-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
            <BarChart3Icon className="size-5 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ອໍເດີສະເລ່ຍຕໍ່ຊົ່ວໂມງ</p>
            <p className="font-semibold text-xl tabular-nums">
              {avgOrdersPerHour.toFixed(1)}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
